<template>
  <div class="sm-plan-details">
    <a
      v-if="showBack"
      href="#"
      class="sm-plan-details__link top"
      @click.prevent="$emit('back')"
    >Back to Plans & Billing</a>
    <div class="sm-plan-details__top">
      <div class="sm-plan-details__top-left">
        <p>Current Plan</p>
        <div>
          <h4>{{plan.title}}</h4>
          <span>
            <template v-if="term == 'monthly'">{{`${formatPrice(plan.subscription_price)}\/month`}}</template>
            <template v-else>{{`${formatPrice(plan.period_amount)}\/${periodAlts[plan.period_unit]}`}}</template>
          </span>
        </div>
      </div>
      <div class="sm-plan-details__top-right">
        <btn-block
          :class="'sm-btn--small sm-btn--switch'"
          :text="'Switch Plan'"
          :icon="'icon-switch'"
          @click="handleSwitchPlan"
        />
        <btn-block 
          v-if="showCancel"
          :class="'sm-btn--small sm-btn--error'" 
          :text="'Cancel Subscription'"
          @click="$emit('cancel-plan')"
        />
      </div>
    </div>
    <div class="sm-plan-details__content">
      <template v-if="plan.mail_service_enabled">
        <h5>Mailbox Services</h5>
        <ul class="sm-plan-details__content-list">
          <li class="sm-plan-details__content-list-item">
            <span class="sm-plan-details__content-list-item-title">Price</span>
            <span class="sm-plan-details__content-list-item-value">
              <template v-if="term == 'monthly'">
                {{`${formatPrice(plan.subscription_price)} / month`}}
              </template>
              <template v-else>
                {{`${formatPrice(plan.period_amount)} / ${periodAlts[plan.period_unit]}`}}
              </template>
            </span>
          </li>

          <details-elem
            v-for="item in planItems.mailbox_services"
            :key="item.field"
            :feature="item"
            :providerCountry="providerCountry"
          />
        </ul>
      </template>

      <template v-if="plan.mail_service_enabled">
        <h5 class="spaced">Facility privileges</h5>
        <ul class="sm-plan-details__content-list">
            <details-elem
              v-for="item in planItems.facility_privileges"
              :key="item.field"
              :feature="item"
              :providerCountry="providerCountry"
            />
        </ul>
      </template>

      <template v-if="plan.phone_service_enabled">
        <h5 class="spaced">SpherePhone&trade; (add-on)</h5>
        <ul class="sm-plan-details__content-list">
          <details-elem
            v-for="item in planItems.spherephone_addon"
            :key="item.field"
            :feature="item"
            :providerCountry="providerCountry"
          />
        </ul>
      </template>

      <template v-if="plan.mail_service_enabled">
        <h5 class="spaced">Extras / Additional services and charges</h5>
        <ul class="sm-plan-details__content-list">
          <details-elem
            v-for="item in planItems.extras"
            :key="item.field"
            :feature="item"
            :providerCountry="providerCountry"
          />
        </ul>
      </template>
      <template v-if="customFeatures.length">
        <ul class="sm-plan-details__content-list custom">
          <details-elem
            v-for="item in customFeatures.map(f => ({...f, available: f.enabled, value: formatPrice(f.price), type: '', suffix: f.term === 'monthly'? 'month' : ''}))"
            :key="item.field_attr"
            :feature="item"
            :providerCountry="providerCountry"
          />
        </ul>
      </template>
    </div>
    <a
      v-if="showBack"
      href="#"
      class="sm-plan-details__link"
      @click.prevent="$emit('back')"
    >Back to Plans & Billing</a>

    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent">
        <div class="sm-plan-details__popup">
          <h4>Can't switch the Plan</h4>
          <p>This customer has no attached payment source or default payment method.</p>
          <btn-block
            :type="'small'"
            :text="'Ok'"
            @click="popup.isOpen = false"
          />
        </div>
      </template>
    </popup-block>
  </div>
</template>

<script>
import {FormatPrice} from '@/utils/general-data';
import PlanFeatures from '@/settings/plan-features';
import PeriodsSettings from '@/settings/periods';
import PlanDetailsElement from '@/components/account/PlanDetailsElement';
import Popup from '@/components/Popup';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'plan-details',
  components: {
    'details-elem': PlanDetailsElement,
    'popup-block': Popup
  },
  props: {
    plan: {
      type: Object,
      default: () => {}
    },
    term: {
      type: String,
      default: 'monthly'
    },
    showBack: {
      type: Boolean,
      default: true
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    hasCreditCards: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      planItems: {
        mailbox_services: [],
        facility_privileges: [],
        spherephone_addon: [],
        extras: [],
      },
      customFeatures: [],
      periodAlts: PeriodsSettings.alts,
      popup: {
        isOpen: false
      },
      providerCountry: "United States"
    }
  },
  computed: {
    ...mapGetters({
      provider: 'provider',
      userAccount: 'account/userAccount',
    })
  },
  created() {
    this.updatePlanItems();
    this.loadCustomFeatures();

    this.getCountryById(this.provider.country_id).then(res => {
      this.providerCountry = res.name
    }).catch(e => {
      console.log(e, e.response);
    });
  },
  methods: {
     ...mapActions({
      getCustomFeatures: 'general/getCustomFeatures',
      getCountryById: 'getCountryById'
    }),
    formatPrice: FormatPrice,
    updatePlanItems() {
      Object.keys(this.planItems).forEach(key => {
        this.planItems[key] = [];

        PlanFeatures[key].forEach(item => {
          let feature = Object.assign({}, item);

          if (!this.plan.hasOwnProperty(feature.field)) {
            if (!feature.alt_field || this.plan.hasOwnProperty(feature.alt_field))
              return;
          }

          if (feature.enable_field && !this.plan[feature.enable_field])
            return;

          if (feature.hidden == 'legacy' || feature.hidden == 'customer')
            return;

          if (feature.ref_from && this.plan[feature.field] == 0 && this.plan[feature.ref_from] == 0)
            return;
            
          if (feature.ref_to && this.plan[feature.field] == 0 && this.plan[feature.ref_to] == 0)
            feature.unlimited = true;

          feature.value = this.plan[feature.field];
          feature.alt_value = feature.alt_field ? this.plan[feature.alt_field] : null;
          feature.flag = feature.flag_field ? this.plan[feature.flag_field] : false;
          feature.available = (!feature.available_field || !this.plan.hasOwnProperty(feature.available_field) || this.plan[feature.available_field]) ? true : false;

          if (feature.customer)
            Object.keys(feature.customer).forEach(key => feature[key] = feature.customer[key]);

          this.planItems[key].push(feature);
        });
      });
    },
    handleSwitchPlan() {
      if (this.provider.client_level != 3 || this.hasCreditCards)
        this.$emit('upgrade-plan');
      else
        this.popup.isOpen = true;
    },
    loadCustomFeatures() {
      if(this.plan) this.getCustomFeatures(this.plan.id).then(res => {
        this.customFeatures = res.custom_features;
      });
    }
  },
  watch: {
    plan() {
      this.updatePlanItems();
      this.loadCustomFeatures();
    },
  }
}
</script>

<style lang="scss">
  @use '@/assets/scss/general/mixins.scss' as *;
  @use '@/assets/scss/framework/scss-variables.scss' as *;
  @use '@/assets/scss/pages/customer/account/components/_plan-details.scss';
</style>